import { useEffect, useMemo, useState } from "react";
import { UseFormSetValue } from "react-hook-form";
import { taskService } from "@miplatform/api";
import {
  ENTITY_TYPES,
  HTTP_STATUSES,
  PLATFORM_MODULES,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IPermission, IWorkOrderTemplate } from "@miplatform/types";
import { groupBy } from "@miplatform/utils";
import { useOrderTemplates, usePermissionsList } from "@mibuildweb/hooks";
import { IRecordPermission, IRoleFormData } from "../../ConfigRoles.types";

type useRoleWorkPermissionsViewModelProps = {
  watchWorkPermissions?: string[];
  setValue: UseFormSetValue<IRoleFormData>;
};

/**
 * Extracts the module name from a URL path.
 *
 * @param path - The URL path in the format `/config/module-label/roles`
 * @returns The module label (second segment after split), or an empty string if not found
 *
 * @example
 * getRoleModuleNameFromPath('/config/mi-dispatch/roles') // returns 'mi-dispatch'
 * getRoleModuleNameFromPath('/config/mi-scheduling/roles') // returns 'mi-scheduling'
 * getRoleModuleNameFromPath('/config') // returns ''
 */
const getRoleModuleNameFromPath = (path: string) => {
  const module = path?.split("/")[2] ?? "";

  return module;
};

const MODULES_PATH: Record<string, string> = {
  dispatch: PLATFORM_MODULES.MI_DISPATCH,
  scheduling: PLATFORM_MODULES.MI_SCHEDULING,
  accounting: PLATFORM_MODULES.MI_ACCOUNTING,
  inspection: PLATFORM_MODULES.MI_INSPECTIONS,
  estimating: PLATFORM_MODULES.MI_ESTIMATING,
};

export const useRoleWorkPermissionsViewModel = ({
  watchWorkPermissions = [],
  setValue,
}: useRoleWorkPermissionsViewModelProps) => {
  const { params, pathname } = useRouter();
  const moduleLabel = getRoleModuleNameFromPath(pathname);
  const module = MODULES_PATH[moduleLabel] ?? "";

  const {
    recordPermissions,
    getRecordPermissions,
    getRoleRecordPermissions,
    roleRecordPermissions,
  } = usePermissionsList();
  const { orderTemplates, getOrderTemplates, setOrderTemplates } =
    useOrderTemplates();
  const [workOrderTemplates, setWorkOrderTemplates] = useState<
    IWorkOrderTemplate[]
  >([]);

  const initialSelectedOrderPermissions = useMemo(() => {
    const res: IPermission[] = [];
    const customOrderPermissions = roleRecordPermissions?.filter((item) =>
      item.name.startsWith(ENTITY_TYPES.ORDER, 0)
    );
    const customWorkOrderPermissions = roleRecordPermissions?.filter((item) =>
      item.name.startsWith(ENTITY_TYPES.WORK_ORDER, 0)
    );

    orderTemplates.forEach((item) => {
      customOrderPermissions?.forEach((cp) => {
        if (cp.entityId === item.id) {
          res.push({
            name: cp.name,
            description: cp.description,
            entityName: item.name,
            entityKey: item.name,
            isVisible: true,
            entityDescription: item?.shortName,
            permissionCategory: ENTITY_TYPES.ORDER,
            permissionCategoryDescription: ENTITY_TYPES.ORDER,
            dependentOnPermissions: [],
            customName: `${item.id}:${cp.name}`,
            module: item.module,
          });
        }
      });
    });

    workOrderTemplates.forEach((item) => {
      customWorkOrderPermissions?.forEach((cp) => {
        if (cp.entityId === item.id) {
          res.push({
            name: cp.name,
            description: cp.description,
            entityName: item.name,
            entityKey: item.name,
            isVisible: true,
            entityDescription: item?.shortName,
            permissionCategory: ENTITY_TYPES.WORK_ORDER,
            permissionCategoryDescription: ENTITY_TYPES.WORK_ORDER,
            dependentOnPermissions: [],
            customName: `${item.id}:${cp.name}`,
            module: item.module,
          });
        }
      });
    });

    return res;
  }, [roleRecordPermissions, orderTemplates, workOrderTemplates]);

  useEffect(() => {
    if (!roleRecordPermissions) {
      return;
    }

    const alreadySelected = initialSelectedOrderPermissions.map(
      (item) => item.customName ?? ""
    );

    setValue("workPermissionNames", alreadySelected, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [roleRecordPermissions, initialSelectedOrderPermissions, setValue]);

  const workOrderPermissions = useMemo(() => {
    const res: IPermission[] = [];
    const customPermissions = recordPermissions?.filter((item) =>
      item.name.startsWith(ENTITY_TYPES.WORK_ORDER, 0)
    );

    workOrderTemplates.forEach((item) => {
      customPermissions?.forEach((cp) => {
        res.push({
          name: cp.name,
          description: cp.description,
          entityName: item.name,
          entityKey: item.name,
          isVisible: true,
          entityDescription: item?.shortName,
          permissionCategory: ENTITY_TYPES.WORK_ORDER,
          permissionCategoryDescription: ENTITY_TYPES.WORK_ORDER,
          dependentOnPermissions: [],
          customName: `${item.id}:${cp.name}`,
          module: item?.module,
        });
      });
    });

    return res;
  }, [recordPermissions, workOrderTemplates]);

  const orderPermissions = useMemo(() => {
    const res: IPermission[] = [];
    const customPermissions = recordPermissions?.filter((item) =>
      item.name.startsWith(ENTITY_TYPES.ORDER, 0)
    );

    orderTemplates.forEach((item) => {
      customPermissions?.forEach((cp) => {
        res.push({
          name: cp.name,
          description: cp.description,
          entityName: item.name,
          entityKey: item.name,
          isVisible: true,
          entityDescription: item?.shortName,
          permissionCategory: ENTITY_TYPES.ORDER,
          permissionCategoryDescription: ENTITY_TYPES.ORDER,
          dependentOnPermissions: [],
          customName: `${item.id}:${cp.name}`,
          module: item?.module,
        });
      });
    });

    return res;
  }, [recordPermissions, orderTemplates]);

  const loadWorkOrderTemplates = async () => {
    const response = await taskService.workOrderTemplates.getAll({
      sort: "name asc",
    });

    if (response.status === HTTP_STATUSES.OK) {
      setWorkOrderTemplates(
        response.data?.map(
          (workOrderType: IWorkOrderTemplate, index: number) => ({
            ...workOrderType,
            index,
          })
        )
      );
    }
  };

  const selectedOrderPermissions = useMemo(() => {
    const res: IPermission[] = [];

    orderPermissions.forEach((permission) => {
      const found = watchWorkPermissions?.find(
        (item: string) => item === permission.customName
      );
      if (found) {
        res.push(permission);
      }
    });

    return res;
  }, [orderPermissions, watchWorkPermissions]);

  const selectedWorkOrderPermissions = useMemo(() => {
    const res: IPermission[] = [];

    workOrderPermissions.forEach((permission) => {
      const found = watchWorkPermissions?.find(
        (item: string) => item === permission.customName
      );
      if (found) {
        res.push(permission);
      }
    });

    return res;
  }, [workOrderPermissions, watchWorkPermissions]);

  const formatRecordPermissions = (
    formData: IRoleFormData
  ): IRecordPermission[] | null => {
    const rawPermissions: {
      entityType: string;
      entityId?: string;
      permission: string;
    }[] = [];
    const { workPermissionNames } = formData;
    workPermissionNames?.forEach((item: string) => {
      const permission = [...orderPermissions, ...workOrderPermissions].find(
        (op) => op.customName === item
      );
      if (permission) {
        rawPermissions.push({
          entityType: permission.permissionCategory,
          entityId: permission?.customName?.split(":")[0],
          permission: permission.name,
        });
      }
    });

    const groupedByEntityType = groupBy(rawPermissions, "entityType") as Record<
      string,
      typeof rawPermissions
    >;
    const recordPermissionsFormatted: IRecordPermission[] = [];

    Object.entries(groupedByEntityType).forEach(([entityType, entries]) => {
      const groupedEntries: Record<string, { permission: string }[]> = groupBy(
        entries,
        "entityId"
      );
      const selections = Object.entries(groupedEntries).map(
        ([entityId, items]) => ({
          entityId,
          permissions: items.map((item) => item.permission),
        })
      );

      recordPermissionsFormatted.push({
        entityType,
        selections,
      });
    });

    return module ? recordPermissionsFormatted : null;
  };

  const loadOrderTemplates = async () => {
    const response = await getOrderTemplates();
    if (response.status === HTTP_STATUSES.OK) {
      setOrderTemplates(response.data);
    }
  };

  useEffect(() => {
    loadOrderTemplates();
    loadWorkOrderTemplates();
    getRecordPermissions();

    if (params.id) {
      getRoleRecordPermissions(params?.id);
    }
  }, []);

  const filterByModule = (permissions: IPermission[]) => {
    return permissions.filter((item) => item.module === module);
  };

  return {
    orderPermissions: filterByModule(orderPermissions),
    workOrderPermissions: filterByModule(workOrderPermissions),
    formatRecordPermissions,
    initialSelectedOrderPermissions,
    recordPermissions,
    selectedOrderPermissions: filterByModule(selectedOrderPermissions),
    module,
    selectedWorkOrderPermissions: filterByModule(selectedWorkOrderPermissions),
  };
};
